/**
 * A live room: hold the connection, render the right surface.
 *
 * `/play/:code` takes a seat; `/watch/:code` watches. Chosen by URL rather than
 * by screen size, because a laptop can be either and guessing wrong puts
 * somebody's hand on the television.
 *
 * This owns the one match connection and nothing else owns one. Two connections
 * to the same match from one browser means two presences, two seats, and a
 * phantom player nobody can remove.
 */

import { useCallback, useMemo, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { connectMatch, emptyView } from '../net/match.js';
import type { MatchHandle, MatchView } from '../net/match.js';
import { joinRoom } from '../net/session.js';
import type { JoinedRoom } from '../net/session.js';
import { useSession } from '../session-context.js';
import { useRecent } from '../use-recent.js';
import { Hand } from './Hand.js';
import { TableScreen } from './TableScreen.js';
import { MarksProvider } from '../render/marks-context.js';
import { SkinProvider } from '../render/skin-context.js';
import { composeSkin, withClassicFaces } from '../render/skin.js';
import { classicFaces, subscribeClassicFaces } from '../render/classic-faces.js';
import { describeError } from './errors.js';

export function Room({ mode }: { mode: 'play' | 'watch' }) {
  const { code } = useParams<{ code: string }>();
  const { session, config } = useSession();

  const [room, setRoom] = useState<JoinedRoom | null>(null);
  const [view, setView] = useState<MatchView>(emptyView());
  const [error, setError] = useState<string | null>(null);
  const handle = useRef<MatchHandle | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!session || !code) return undefined;
    let cancelled = false;

    void (async () => {
      try {
        const joined = await joinRoom(session, code);
        if (cancelled) return;
        setRoom(joined);

        const live = await connectMatch(session, joined.matchId, setView, mode === 'watch');
        if (cancelled) {
          // The route changed while we were connecting. Leaving here is what
          // stops an abandoned presence holding a seat.
          void live.leave();
          return;
        }
        handle.current = live;
      } catch (caught) {
        if (!cancelled) setError(describeError(caught, 'That room could not be found.'));
      }
    })();

    return () => {
      cancelled = true;
      void handle.current?.leave();
      handle.current = null;
    };
  }, [session, code, mode]);

  /*
   * A closing room sends everybody somewhere they can start again - and that
   * is a different place depending on who they are.
   *
   * The host is the one who can open a table, so they go to their games and
   * can pick the next one. Everybody else cannot start anything; what they
   * need is the box to type the next room code into, which is the landing
   * page. Sending both to the same place left one of them a click short.
   *
   * A room closes when the host ends the game, and when a paid room's host
   * leaves after the last hand. Leaving people sitting in a lobby that will
   * never deal again is the failure this avoids - they would wait, then
   * reload, then ask why.
   */
  useEffect(() => {
    if (!view.closing) return undefined;
    const timer = window.setTimeout(() => {
      navigate(view.isHost ? '/library' : '/', { replace: true });
    }, 4000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [view.closing, view.isHost, navigate]);

  /*
   * A removed player is sent to the landing page.
   *
   * The server drops their socket straight after this error, so nothing else
   * is coming: no further view, no reconnect worth waiting for. Short, because
   * unlike a room closing there is no result on screen worth reading - the
   * banner says what happened and that is all there is.
   */
  useEffect(() => {
    if (view.lastError?.code !== 'REMOVED') return undefined;
    const timer = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 2500);
    return () => {
      window.clearTimeout(timer);
    };
  }, [view.lastError, navigate]);

  /*
   * A room that is not coming back sends you somewhere you can start again.
   *
   * Distinct from `closing`, which is a room ending on purpose and worth
   * pausing on. This is a room that has simply gone - the match was terminated
   * while everybody sat looking at it - so there is nothing to read and no
   * reason to linger. The banner says what happened on the way out.
   */
  useEffect(() => {
    if (!view.gone) return undefined;
    const timer = window.setTimeout(() => {
      navigate(view.isHost ? '/library' : '/', { replace: true });
    }, 4000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [view.gone, view.isHost, navigate]);

  /*
   * The room changed game, so what we were handed at the join is now a layout
   * for a game nobody is playing.
   *
   * The layout, the zones and the variant declarations are static FOR A GAME,
   * which is why they ride with the join rather than with every packet - but a
   * room outlives the game inside it. Asking again is cheap and keeps that
   * property: sent once per game, rather than once per session.
   */
  useEffect(() => {
    if (!session || !code || !room) return undefined;
    if (!view.gameId || view.gameId === room.gameId) return undefined;

    let cancelled = false;
    void (async () => {
      try {
        const joined = await joinRoom(session, code);
        if (!cancelled) setRoom(joined);
      } catch {
        // The next packet will ask again. A stale layout is better than a
        // screen that has given up.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, code, room, view.gameId]);

  const onMove = useCallback((move: string, args: string[]) => {
    handle.current?.send(move, args);
  }, []);

  /*
   * Walking away on purpose (H21).
   *
   * Nothing to send: the effect that owns the connection already calls
   * `leave()` when this screen unmounts, and the server HOLDS the seat rather
   * than freeing it - so the room carries on with the grace period and
   * auto-play it already has for somebody whose phone died. A deliberate exit
   * and a flat battery are the same event to everyone else; the only
   * difference is that this one does not make the table wait to find out.
   *
   * Host to their games, everyone else to the front door, matching where a
   * closed or vanished room already sends them.
   */
  const onLeave = useCallback(() => {
    navigate(view.isHost ? '/library' : '/', { replace: true });
  }, [navigate, view.isHost]);

  const onHostAction = useCallback((action: Record<string, unknown>) => {
    handle.current?.host(action);
  }, []);

  const onSay = useCallback((phraseId: string) => {
    handle.current?.say(phraseId);
  }, []);

  const onType = useCallback((text: string) => {
    handle.current?.type(text);
  }, []);

  const onWear = useCallback((avatarId: string) => {
    handle.current?.wear(avatarId);
  }, []);

  /* A host changed their deck or table. The match caches what people wear at
     the moment they join, so it has to be told to look again. */
  const onLookAgain = useCallback(() => {
    handle.current?.lookAgain();
  }, []);

  /*
   * What the voice control needs, or null where voice is not offered.
   *
   * Null rather than a disabled button when the deployment has no STUN: almost
   * nothing would connect, and a button that never works is worse than none.
   * The session's own user id is what a peer is addressed by, so it comes from
   * here rather than from the match view — which knows about seats.
   */
  const voice = useMemo(() => {
    const ice = config?.voice;
    if (!ice?.available || !session?.user_id) return null;
    return {
      myId: session.user_id,
      iceServers: ice.iceServers,
      roster: view.voice,
      signal: (to: string, kind: 'offer' | 'answer' | 'candidate' | 'bye', payload: string) => {
        handle.current?.signal(to, kind, payload);
      },
      onSignal: (handler: ((from: string, kind: string, payload: string) => void) | null) => {
        handle.current?.onSignal(handler);
      },
      setVoice: (state: { joined: boolean; micOff: boolean; earsOff: boolean }) => {
        handle.current?.setVoice(state);
      },
    };
  }, [config?.voice, session?.user_id, view.voice]);

  /*
   * Noted as played once the cards are out.
   *
   * A deal, not a door: opening a table and backing out is not playing, and a
   * list of rooms somebody glanced at is a worse answer to "what was I
   * playing" than no list. `remember` ignores a game already at the front, so
   * this running on every packet costs one write per game rather than one per
   * packet.
   */
  /*
   * What this table is painted in, from the host (D87).
   *
   * Memoised on the two ids rather than on `view.skin`, which is a fresh
   * object every packet - the provider rebuilds its custom properties whenever
   * the skin's identity changes, and a new one per tick would do that sixty
   * times a minute for paint that has not moved.
   */
  /*
   * Local, and it wins (C7).
   *
   * Subscribed rather than read once, because the switch is in the menu of
   * this very table and the cards have to change under it. An accessibility
   * setting that needed a reload would be one nobody found twice.
   */
  const classic = useSyncExternalStore(subscribeClassicFaces, classicFaces, () => false);

  const skin = useMemo(() => {
    const painted = composeSkin(view.skin?.deckId, view.skin?.tableId);
    return classic ? withClassicFaces(painted) : painted;
  }, [view.skin?.deckId, view.skin?.tableId, classic]);

  const recent = useRecent();
  useEffect(() => {
    if (view.lifecycle === 'playing' && room?.gameId) recent.remember(room.gameId);
  }, [view.lifecycle, room?.gameId, recent]);

  if (error) {
    return <CannotJoin message={error} />;
  }

  const layout = room?.layout ?? null;
  if (!room || !layout) return <div className="splash">Finding the table…</div>;

  /*
   * What this game wrote on its cards, shared with everything below.
   *
   * Here rather than in `Hand` and `TableScreen` separately, because a card is
   * drawn from both and from the flight layer between them - and a two that
   * was marked in your hand and unmarked in mid-air would be worse than no
   * mark at all.
   */
  if (mode === 'watch') {
    return (
      <SkinProvider skin={skin}>
      <MarksProvider marks={layout.cardMarks}>
        <TableScreen
          view={view}
          layout={layout}
          zones={room.zones}
          roomCode={(code ?? '').toUpperCase()}
          gameName={room.gameName}
        />
      </MarksProvider>
      </SkinProvider>
    );
  }

  return (
    <SkinProvider skin={skin}>
    <MarksProvider marks={layout.cardMarks}>
      <Hand
        view={view}
        layout={layout}
        zones={room.zones}
        roomCode={(code ?? '').toUpperCase()}
      gameName={room.gameName}
      objective={room.objective ?? ''}
      howToPlay={room.howToPlay ?? []}
      variantSpecs={room.variants ?? []}
      onMove={onMove}
      onHostAction={onHostAction}
      onLeave={onLeave}
      onSay={onSay}
      onType={onType}
      onWear={onWear}
      onLookAgain={onLookAgain}
      voice={voice}
        chatLog={view.chatLog}
        allowTextChat={view.allowTextChat}
        turnLimits={config?.turnLimitMs}
      />
    </MarksProvider>
    </SkinProvider>
  );
}

/**
 * A room that is not there, and the way back.
 *
 * Counted down rather than left sitting: a mistyped code is the common cause,
 * and the thing to do about it is type another one. Somebody who reads the
 * message and does nothing should end up where they can, without having to
 * work out that the underlined word is the way out.
 *
 * The button stays, because a countdown you cannot skip is its own annoyance.
 */
function CannotJoin({ message }: { message: string }) {
  const navigate = useNavigate();
  const [left, setLeft] = useState(8);

  useEffect(() => {
    if (left <= 0) {
      navigate('/', { replace: true });
      return undefined;
    }
    const timer = window.setTimeout(() => setLeft((value) => value - 1), 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [left, navigate]);

  return (
    <div className="page narrow">
      <h1 className="page-title">Can&apos;t join</h1>
      <p className="banner error">{message}</p>
      <p className="field-help" role="status">
        Returning to try another code in {String(left)}…
      </p>
      <Link className="big-button primary" to="/">
        Try another code now
      </Link>
    </div>
  );
}
